<script setup>
import { computed, ref } from 'vue'
import moment from 'moment-timezone'
import { numberWithCommas } from '@/plugins/misc'
import { useReservationsStore } from '@/stores'
import { storeToRefs } from 'pinia'

const reservationsStore = useReservationsStore()
const { insights, insightsLoading } = storeToRefs(reservationsStore)

// Expand/collapse state
const showByTimePeriod = ref(true)
const showByRestaurant = ref(false)
const showByGroup = ref(false)
const expandedRestaurants = ref({})
const expandedGroups = ref({})
const expandedGroupRestaurants = ref({})

const byTimePeriod = computed(() => insights.value?.byTimePeriod || [])
const byRestaurant = computed(() => insights.value?.byRestaurant || [])
const byGroup = computed(() => (insights.value?.byGroup || []).filter(g => g.group !== 'Other'))

const hasData = computed(() => {
  return byTimePeriod.value.some(p => p.bookingCount > 0)
})

const formatCurrency = val => {
  if (!val) return '$0'
  
  return '$' + numberWithCommas(Math.round(val))
}

const toggleRestaurant = key => {
  expandedRestaurants.value[key] = !expandedRestaurants.value[key]
}

const toggleGroup = key => {
  expandedGroups.value[key] = !expandedGroups.value[key]
}

const toggleGroupRestaurant = key => {
  expandedGroupRestaurants.value[key] = !expandedGroupRestaurants.value[key]
}
</script>

<template>
  <div class="booking-insights">
    <!-- Loading -->
    <div
      v-if="insightsLoading && !insights"
      class="text-center py-8"
    >
      <VProgressCircular
        indeterminate
        color="primary"
      />
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!hasData"
      class="text-center py-8 text-medium-emphasis text-sm"
    >
      No booking insights yet. Insights will appear after your first completed reservation.
    </div>

    <!-- Insights content -->
    <div v-else>
      <!-- By Time Period -->
      <div class="insight-section">
        <div
          class="insight-header"
          @click="showByTimePeriod = !showByTimePeriod"
        >
          <span class="text-sm font-weight-bold">Summary</span>
          <VBtn
            density="compact"
            size="small"
            variant="text"
            color="secondary"
            :icon="showByTimePeriod ? 'mdi-chevron-up' : 'mdi-chevron-down'"
          />
        </div>
        <VExpandTransition>
          <div v-if="showByTimePeriod">
            <div class="insight-cards">
              <div
                v-for="period in byTimePeriod"
                :key="period.period"
                class="insight-card content-card"
              >
                <div class="insight-card-title text-xs text-medium-emphasis">
                  {{ period.period }}
                </div>
                <div class="insight-card-stats">
                  <div class="stat-item">
                    <div class="stat-value">
                      {{ period.bookingCount }}
                    </div>
                    <div class="stat-label text-xs text-medium-emphasis">
                      Bookings
                    </div>
                  </div>
                  <div class="stat-item">
                    <div class="stat-value">
                      {{ formatCurrency(period.totalSpend) }}
                    </div>
                    <div class="stat-label text-xs text-medium-emphasis">
                      Guaranteed F&amp;B Spend
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </VExpandTransition>
      </div>

      <!-- By Restaurant -->
      <div class="insight-section">
        <div
          class="insight-header"
          @click="showByRestaurant = !showByRestaurant"
        >
          <span class="text-sm font-weight-bold">By Restaurant</span>
          <VBtn
            density="compact"
            size="small"
            variant="text"
            color="secondary"
            :icon="showByRestaurant ? 'mdi-chevron-up' : 'mdi-chevron-down'"
          />
        </div>
        <VExpandTransition>
          <div v-if="showByRestaurant">
            <div
              v-for="r in byRestaurant"
              :key="`${r.restaurantName}||${r.market}`"
              class="restaurant-insight content-card mb-2"
            >
              <div
                class="restaurant-insight-header"
                @click="toggleRestaurant(`${r.restaurantName}||${r.market}`)"
              >
                <div class="restaurant-info">
                  <div class="text-sm font-weight-bold">
                    {{ r.restaurantName }}
                  </div>
                  <div class="text-xs text-medium-emphasis">
                    {{ r.market }}
                    <span v-if="r.group !== 'Other'"> &middot; {{ r.group }}</span>
                  </div>
                </div>
                <div class="restaurant-summary text-right">
                  <div class="text-sm">
                    {{ r.bookingCount }} {{ r.bookingCount === 1 ? 'booking' : 'bookings' }}
                  </div>
                  <div class="text-xs text-medium-emphasis">
                    {{ formatCurrency(r.totalSpend) }}
                  </div>
                </div>
              </div>
              <VExpandTransition>
                <div v-if="expandedRestaurants[`${r.restaurantName}||${r.market}`]">
                  <VDivider class="my-2" />
                  <div class="restaurant-bookings">
                    <div
                      v-for="(b, idx) in r.bookings"
                      :key="idx"
                      class="booking-row text-xs"
                    >
                      <span class="text-medium-emphasis">{{ moment(b.date).format('MM-DD-YY') }}</span>
                      <span
                        v-if="b.partySize"
                        class="booking-row-party"
                      >Party of {{ b.partySize }}</span>
                      <span>{{ formatCurrency(b.spend) }}</span>
                    </div>
                  </div>
                </div>
              </VExpandTransition>
            </div>
          </div>
        </VExpandTransition>
      </div>

      <!-- By Group -->
      <div class="insight-section">
        <div
          class="insight-header"
          @click="showByGroup = !showByGroup"
        >
          <span class="text-sm font-weight-bold">By Restaurant Group</span>
          <VBtn
            density="compact"
            size="small"
            variant="text"
            color="secondary"
            :icon="showByGroup ? 'mdi-chevron-up' : 'mdi-chevron-down'"
          />
        </div>
        <VExpandTransition>
          <div v-if="showByGroup">
            <div
              v-for="g in byGroup"
              :key="g.group"
              class="group-insight content-card mb-2"
            >
              <div
                class="d-flex justify-space-between align-center cursor-pointer"
                @click="toggleGroup(g.group)"
              >
                <div>
                  <div class="text-sm font-weight-bold">
                    {{ g.group }}
                  </div>
                  <div class="text-xs text-medium-emphasis">
                    {{ g.restaurantCount }} {{ g.restaurantCount === 1 ? 'restaurant' : 'restaurants' }}
                  </div>
                </div>
                <div class="text-right">
                  <div class="text-sm">
                    {{ g.bookingCount }} {{ g.bookingCount === 1 ? 'booking' : 'bookings' }}
                  </div>
                  <div class="text-xs text-medium-emphasis">
                    {{ formatCurrency(g.totalSpend) }}
                  </div>
                </div>
              </div>
              <VExpandTransition>
                <div v-if="expandedGroups[g.group]">
                  <VDivider class="my-2" />
                  <div class="group-restaurants">
                    <div
                      v-for="gr in g.restaurants"
                      :key="`${g.group}||${gr.restaurantName}||${gr.market}`"
                    >
                      <div
                        class="booking-row text-xs cursor-pointer"
                        @click="toggleGroupRestaurant(`${g.group}||${gr.restaurantName}||${gr.market}`)"
                      >
                        <span>
                          {{ gr.restaurantName }}
                          <span class="text-medium-emphasis"> &middot; {{ gr.market }}</span>
                        </span>
                        <span>{{ gr.bookingCount }} {{ gr.bookingCount === 1 ? 'booking' : 'bookings' }}</span>
                        <span class="booking-row-spend">{{ formatCurrency(gr.totalSpend) }}</span>
                      </div>
                      <VExpandTransition>
                        <div
                          v-if="expandedGroupRestaurants[`${g.group}||${gr.restaurantName}||${gr.market}`]"
                          class="group-restaurant-bookings"
                        >
                          <div
                            v-for="(b, idx) in gr.bookings"
                            :key="idx"
                            class="booking-row text-xs"
                          >
                            <span class="text-medium-emphasis">{{ moment(b.date).format('MM-DD-YY') }}</span>
                            <span
                              v-if="b.partySize"
                              class="booking-row-party"
                            >Party of {{ b.partySize }}</span>
                            <span class="booking-row-spend">{{ formatCurrency(b.spend) }}</span>
                          </div>
                        </div>
                      </VExpandTransition>
                    </div>
                  </div>
                </div>
              </VExpandTransition>
            </div>
          </div>
        </VExpandTransition>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.booking-insights {
  padding: 0 4px;
}

.insight-section {
  margin-bottom: 12px;
}

.insight-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 8px 0;
}

.insight-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 8px;
}

.insight-card {
  padding: 12px;
  border-radius: 8px;
}

.insight-card-title {
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.insight-card-stats {
  display: flex;
  gap: 16px;
}

.stat-item {
  text-align: left;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
  color: var(--ds-brand-primary-dark);
}

.stat-label {
  margin-top: 2px;
}

.restaurant-insight,
.group-insight {
  padding: 12px;
  border-radius: 8px;
}

.restaurant-insight-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}

.restaurant-bookings {
  padding: 0 4px 4px;
}

.booking-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  > span:first-child {
    width: 70px;
    flex-shrink: 0;
  }

  .booking-row-party {
    text-align: center;
  }

  > span:last-child {
    width: 70px;
    flex-shrink: 0;
    text-align: right;
  }

  &:last-child {
    border-bottom: none;
  }
}

.group-restaurants {
  padding: 0 4px 4px;

  > div > .booking-row > span:first-child {
    width: auto;
    flex: 1;
  }

  .booking-row-spend {
    width: 70px;
    flex-shrink: 0;
    text-align: right;
  }
}

.group-restaurant-bookings {
  padding: 0 0 4px 16px;

  .booking-row > span:first-child {
    width: 70px;
    flex-shrink: 0;
  }

  .booking-row-party {
    text-align: center;
  }

  .booking-row-spend {
    width: 70px;
    flex-shrink: 0;
    text-align: right;
  }
}

@media (max-width: 600px) {
  .insight-cards {
    grid-template-columns: 1fr;
  }
}
</style>
